import React, { useEffect, useRef, useState } from "react";
import CloseIcon from "../images/CloseIcon";

// The element that last lost focus: a save pressed with Enter blurs its field before this popup opens.
let lastBlurred: HTMLElement | null = null;
if (typeof document !== "undefined") document.addEventListener("focusout", (e) => { lastBlurred = e.target as HTMLElement; }, true);

interface PopupLocationProps {
  show: boolean;
  title: string;
  desc?: string;
  onClose: () => void;
  onOpen: () => void;
}

const PopupLocation: React.FC<PopupLocationProps> = ({
  show,
  title,
  onOpen,
  onClose,
  desc,
}) => {
  const [showPopup, setShowPopup] = useState(show);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setShowPopup(show);
  }, [show]);
  // Focus Confirm while open (Enter/Escape close it), then give focus back to where it was.
  useEffect(() => {
    if (showPopup) {
      const active = document.activeElement as HTMLElement | null;
      returnFocus.current = active && active !== document.body ? active : lastBlurred;
      confirmRef.current?.focus();
    } else if (returnFocus.current?.isConnected) {
      returnFocus.current.focus();
      returnFocus.current = null;
    }
  }, [showPopup]);
  const close = () => {
    setShowPopup(false);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal={showPopup}
      aria-label={title}
      aria-hidden={!showPopup}
      onKeyDown={(e) => {
        if (e.key === "Escape" && showPopup) {
          e.stopPropagation();
          close();
        }
      }}
      className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showPopup ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} transition-opacity duration-300 ease-in-out`}
      style={{
        backgroundColor: "#22252552",
        zIndex: "200",
        // Hidden only after the fade-out, and at once on open so Confirm can take focus.
        visibility: showPopup ? "visible" : "hidden",
        transition: showPopup ? "opacity 0.3s ease" : "opacity 0.3s ease, visibility 0s linear 0.3s",
        width: "100%",
      }}
    >
      <div
        className={`transform transition-transform duration-300 ease-in-out ${showPopup ? "translate-y-0" : "translate-y-4 opacity-0"}`}
        style={{
          padding: "24px",
          borderRadius: "24px",
          backgroundColor: "#FFFFFF",
          width: "530px",
          transition: "transform 0.3s ease, opacity 0.3s ease",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="text-[16px] font-semibold font-inter text-main leading-[24px]">
            {title}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={close}
            className="flex items-center justify-center cursor-pointer"
            style={{
              width: "24px",
              height: "24px",
              borderRadius: "100%",
              backgroundColor: "#D1E4E5",
            }}
          >
            <CloseIcon />
          </button>
        </div>
        <div
          className="flex items-center justify-center"
          style={{ padding: "20px 16px" }}
        >
          <div className="text-[16px] font-semibold font-inter text-main leading-[24px]">
            {desc
              ? desc
              : "Please select location in the upper left corner to continue creating."}
          </div>
        </div>
        <div
          className="flex items-center justify-center"
          style={{ padding: "0px 16px 12px 16px" }}
        >
          <button
            type="button"
            ref={confirmRef}
            onClick={close}
            className="cursor-pointer w-1/2 text-[14px] leading-[22px] font-inter flex items-center justify-center font-medium"
            style={{
              borderRadius: "6px",
              height: "40px",
              backgroundColor: "#3AC0C4",
              color: "#F9F9F9",
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopupLocation;
